import { apiClient } from '@/api/client';
import type {
  CreateApplicationRequest,
  CreateApplicationResponse,
  GetApplicationsResponse,
  GetApplicationsParams,
  GetApplicationResponse,
  UpdateApplicationStatusRequest,
  UpdateApplicationStatusResponse,
  DeleteApplicationResponse,
  ApplicationData,
} from '@/types/api';

/**
 * POST /api/applications
 * Create a new application for a tuition case
 * Requires JWT authentication and tutor profile
 * Returns both the application and updated case data
 */
export const createApplication = async (
  request: CreateApplicationRequest
): Promise<CreateApplicationResponse['data']> => {
  const response = await apiClient.post<CreateApplicationResponse>('/applications', request);

  if (!response.data.success) {
    throw new Error('Failed to create application');
  }

  return response.data.data;
};

/**
 * GET /api/applications/my-applications
 * Get all applications submitted by the current tutor with pagination
 * Requires JWT authentication
 */
export const getMyApplications = async (
  params?: GetApplicationsParams
): Promise<GetApplicationsResponse['data']> => {
  const response = await apiClient.get<GetApplicationsResponse>('/applications/my-applications', {
    params,
  });

  if (!response.data.success) {
    throw new Error('Failed to fetch applications');
  }

  return response.data.data;
};

/**
 * GET /api/applications/:applicationId
 * Get a single application by ID
 * Requires JWT authentication
 */
export const getApplication = async (applicationId: string): Promise<ApplicationData> => {
  const response = await apiClient.get<GetApplicationResponse>(`/applications/${applicationId}`);

  if (!response.data.success) {
    throw new Error('Failed to fetch application');
  }

  return response.data.data;
};

/**
 * PATCH /api/applications/:applicationId/status
 * Update application status (for case owners/parents only)
 * Requires JWT authentication
 */
export const updateApplicationStatus = async (
  applicationId: string,
  request: UpdateApplicationStatusRequest
): Promise<ApplicationData> => {
  const response = await apiClient.patch<UpdateApplicationStatusResponse>(
    `/applications/${applicationId}/status`,
    request
  );

  if (!response.data.success) {
    throw new Error('Failed to update application status');
  }

  return response.data.data;
};

/**
 * DELETE /api/applications/:applicationId
 * Delete (withdraw) an application (tutors can withdraw their own applications)
 * Requires JWT authentication
 */
export const deleteApplication = async (applicationId: string): Promise<DeleteApplicationResponse['data']> => {
  const response = await apiClient.delete<DeleteApplicationResponse>(`/applications/${applicationId}`);

  if (!response.data.success) {
    throw new Error('Failed to delete application');
  }

  return response.data.data;
};

